import equal from 'fast-deep-equal';

export type PresenceDoc = Record<string, unknown>;

type Slice = {
  owner: symbol;
  data: unknown;
  // Bound at write time, so the slice's type is captured by the closure and the map can erase it.
  project?: () => PresenceDoc;
};

/**
 * The connection's presence document, composed from named slices.
 *
 * The protocol stores one full-replacement document per connection and channel, so independent
 * feature writers cannot send unrelated root documents safely. Each mounted presence hook owns a
 * named slice under an ownership token; composition merges them.
 *
 * `document` keeps its identity while the slices are unchanged, which is what lets the caller
 * decide whether a frame is owed by comparing references.
 */
export class PresenceDocument {
  private slices = new Map<string, Slice>();
  private composed: PresenceDoc | null = null;
  private stale = false;

  /**
   * Set or remove one owner's slice. Returns whether the document changed: re-setting a slice to a
   * deep-equal value is what a cell selection re-emitted on every render looks like, and it owes
   * the server nothing.
   */
  set<T>(owner: symbol, name: string, data: T | null, legacyProjection?: (data: T) => PresenceDoc): boolean {
    const current = this.slices.get(name);

    if (data === null) {
      // Only the holder may clear it, so a released slice cannot be cleared by a later owner.
      if (!current || current.owner !== owner) return false;
      this.slices.delete(name);
    } else {
      if (current && current.owner !== owner) {
        console.error(`[Realtime] Multiple local presence writers for slice "${name}"; latest wins.`);
      } else if (current && equal(current.data, data)) {
        return false;
      }
      this.slices.set(name, { owner, data, project: legacyProjection && (() => legacyProjection(data)) });
    }

    this.stale = true;
    return true;
  }

  clear(): boolean {
    if (this.slices.size === 0) return false;
    this.slices.clear();
    this.stale = true;
    return true;
  }

  get document(): PresenceDoc | null {
    if (this.stale) {
      this.composed = this.compose();
      this.stale = false;
    }
    return this.composed;
  }

  private compose(): PresenceDoc | null {
    if (this.slices.size === 0) return null;

    const document: PresenceDoc = {};
    // Flat projections first, so a namespaced slice always wins over a shim writing the same key.
    for (const slice of this.slices.values()) {
      if (slice.project) Object.assign(document, slice.project());
    }
    for (const [name, slice] of this.slices) {
      document[name] = slice.data;
    }
    return document;
  }
}
